import React, { useState, useEffect } from "react";
import { X } from "lucide-react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { getAllSlabsForMerchant } from "../../../../services/slabs";
import { setComission } from "../../../../services/agent_management_service";

const AssignSlabModal = ({ open, onClose, refetch, agentData }) => {
  const { t } = useTranslation();
  const [selectedSlab, setSelectedSlab] = useState(null);


  const { data: slabsData, isLoading: loadingSlabs } = useQuery({
    queryKey: ["merchantSlabsForAgents"],
    queryFn: () => getAllSlabsForMerchant({ 
      slabFor: "agent",
      limit: 100 
    }),
    enabled: open,
  });


  const setCommissionMutation = useMutation({
    mutationFn: ({ agentId, slabId }) => 
      setComission(agentId, { slabId }), 
    onSuccess: (data) => {
      toast.success(data.message || t('agentManagement.slabAssignedSuccess'));
      refetch();
      onClose();
      resetForm();
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || t('agentManagement.slabAssignError'));
      console.log(error, "this is error")
    }
  });

  const resetForm = () => {
    setSelectedSlab(null);
  };

  useEffect(() => {
    if (agentData && open) {
      if (agentData.commissionRateDetails) {
        setSelectedSlab(agentData.commissionRateDetails);
      }
    }
  }, [agentData, open]);

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleAssignSlab = () => {
    if (!selectedSlab) {
      toast.error(t('agentManagement.selectSlab'));
      return;
    }

    setCommissionMutation.mutate({
      agentId: agentData.user.uid,
      slabId: selectedSlab.id 
    });
  };

  const handleSlabSelect = (slab) => {
    setSelectedSlab(slab);
  };


  const isCurrentlyAssignedSlab = (slab) => {
    return agentData?.commissionRateDetails?.id === slab.id;
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/20 bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-md">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            {t('agentManagement.assignSlab')}
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <X size={24} />
          </button>
        </div>


        <div className="p-6">
    
          {agentData && (
            <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <h3 className="font-medium text-gray-900 dark:text-white mb-2">
                {agentData.user?.username}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                {agentData.user?.email}
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                {t('agentManagement.currentCommission')}: {parseFloat(agentData.commissionRateDetails?.percentage || 0).toFixed(2)}%
               
              </p>
            </div>
          )}

         
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              {t('agentManagement.selectSlab')}
            </label>
            
            {loadingSlabs ? (
              <div className="text-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-red-600 mx-auto"></div>
              </div>
            ) : (
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {slabsData?.data?.map((slab) => (
                  <div
                    key={slab.id}
                    onClick={() => handleSlabSelect(slab)}
                    className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                      selectedSlab?.id === slab.id
                        ? "border-red-500 bg-red-50 dark:bg-red-900/20"
                        : isCurrentlyAssignedSlab(slab)
                        ? "border-green-500 bg-green-50 dark:bg-green-900/20"
                        : "border-gray-200 dark:border-gray-600 hover:border-red-300"
                    }`}
                  >
                    <div className="flex justify-between items-center">
                      <div>
                        <h4 className="font-medium text-gray-900 dark:text-white">
                          {slab.slabTypeDetails?.title}
                          {isCurrentlyAssignedSlab(slab) && (
                            <span className="ml-2 text-xs bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 px-2 py-1 rounded">
                              {t('agentManagement.currentlyAssigned')}
                            </span>
                          )}
                        </h4>
                        <p className="text-sm text-gray-600 dark:text-gray-300">
                          {parseFloat(slab.percentage).toFixed(2)}% 
                          {slab.countryDetails && ` • ${slab.countryDetails.countryName}`}
                          {slab.providerDetails && ` • ${slab.providerDetails.companyName}`}
                        </p>
                      </div>
                      <div className="flex items-center space-x-2">
                        {isCurrentlyAssignedSlab(slab) && (
                          <span className="text-green-600 dark:text-green-400 text-xs">
                            ✓
                          </span>
                        )}
                        <div
                          className={`w-4 h-4 rounded-full border-2 ${
                            selectedSlab?.id === slab.id
                              ? "bg-red-500 border-red-500"
                              : "border-gray-300"
                          }`}
                        />
                      </div>
                    </div>
                  </div>
                ))}
                
                {slabsData?.data?.length === 0 && (
                  <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                    {t('agentManagement.noSlabsAvailable')}
                  </div>
                )}
              </div>
            )}
          </div>


          {selectedSlab && (
            <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-2">
                {t('agentManagement.selectedSlab')}
              </h4>
              <p className="text-sm text-blue-800 dark:text-blue-200">
                <strong>{selectedSlab.slabTypeDetails?.title}</strong> - {parseFloat(selectedSlab.percentage).toFixed(2)}%
              </p>
              {/* <p className="text-sm text-blue-800 dark:text-blue-200 mt-1">
                {t('agentManagement.slabAutoUpdate')}
              </p> */}
              {selectedSlab.countryDetails && (
                <p className="text-sm text-blue-800 dark:text-blue-200">
                  {t('slabManagement.country')}: {selectedSlab.countryDetails.countryName}
                </p>
              )}
              {selectedSlab.providerDetails && (
                <p className="text-sm text-blue-800 dark:text-blue-200">
                  {t('slabManagement.provider')}: {selectedSlab.providerDetails.companyName}
                </p>
              )}
            </div>
          )}
        </div>

 
        <div className="flex justify-end space-x-3 p-6 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
            disabled={setCommissionMutation.isLoading}
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={handleAssignSlab}
            disabled={setCommissionMutation.isLoading || !selectedSlab}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {setCommissionMutation.isLoading ? (
              <div className="flex items-center space-x-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>{t('common.assigning')}...</span>
              </div>
            ) : (
              t('agentManagement.assignSlab')
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AssignSlabModal;